import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaTimes, FaPlus, FaEdit, FaTrash, FaCheck, FaUser,
  FaSpinner, FaToggleOn, FaToggleOff, FaSearch,
} from 'react-icons/fa';
import './ModalGestaoPagadores.css';

/**
 * CRUD de pagadores — EXCLUSIVO admin_geral (a renderização é gated no pai,
 * e o backend reforça a permissão de escrita).
 * onChange() é chamado após qualquer alteração para o pai recarregar a lista.
 */
export default function ModalGestaoPagadores({ isOpen, onClose, onChange }) {
  const [lista,   setLista]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [busca,   setBusca]   = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(true);

  const [novo, setNovo] = useState({ nome: '', documento: '', observacao: '' });
  const [editId,   setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', documento: '', observacao: '' });

  const fetchLista = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/financeiro/pagadores', {
        params: { incluir_inativos: 1 },
      });
      setLista(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erro ao carregar pagadores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLista();
      setNovo({ nome: '', documento: '', observacao: '' });
      setEditId(null);
      setBusca('');
    }
  }, [isOpen, fetchLista]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const filtrados = useMemo(() => {
    let arr = lista;
    if (!mostrarInativos) arr = arr.filter((p) => p.ativo);
    const q = busca.trim().toLowerCase();
    if (q) {
      arr = arr.filter(
        (p) =>
          p.nome.toLowerCase().includes(q) ||
          String(p.documento || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [lista, mostrarInativos, busca]);

  const notifyParent = () => onChange?.();

  const handleCriar = async () => {
    const nome = novo.nome.trim();
    if (!nome) { toast.warn('Informe o nome do pagador.'); return; }
    setSaving(true);
    try {
      await api.post('/api/financeiro/pagadores', {
        nome,
        documento:  novo.documento.trim()  || null,
        observacao: novo.observacao.trim() || null,
      });
      toast.success('Pagador cadastrado!');
      setNovo({ nome: '', documento: '', observacao: '' });
      await fetchLista();
      notifyParent();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao cadastrar pagador.');
    } finally {
      setSaving(false);
    }
  };

  const iniciarEdicao = (p) => {
    setEditId(p.id);
    setEditForm({ nome: p.nome, documento: p.documento || '', observacao: p.observacao || '' });
  };

  const salvarEdicao = async (id) => {
    const nome = editForm.nome.trim();
    if (!nome) { toast.warn('Nome é obrigatório.'); return; }
    setSaving(true);
    try {
      await api.put(`/api/financeiro/pagadores/${id}`, {
        nome,
        documento:  editForm.documento.trim()  || null,
        observacao: editForm.observacao.trim() || null,
      });
      toast.success('Pagador atualizado.');
      setEditId(null);
      await fetchLista();
      notifyParent();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao atualizar.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (p) => {
    setSaving(true);
    try {
      await api.put(`/api/financeiro/pagadores/${p.id}`, { ativo: p.ativo ? 0 : 1 });
      await fetchLista();
      notifyParent();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao alterar status.');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (p) => {
    if (!window.confirm(
      `Excluir o pagador "${p.nome}"?\n\n` +
      `Se houver lançamentos vinculados, ele será apenas inativado para preservar o histórico.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/api/financeiro/pagadores/${p.id}`);
      toast.success(data?.message || 'Operação concluída.');
      await fetchLista();
      notifyParent();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao excluir.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mgp-overlay" onMouseDown={onClose}>
      <div
        className="mgp-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Gestão de pagadores"
      >
        {/* Header */}
        <div className="mgp-header">
          <div className="mgp-header-title">
            <span className="mgp-header-icon"><FaUser /></span>
            <div>
              <h2>Pagadores</h2>
              <p>Cadastro e gestão · somente Admin Geral</p>
            </div>
          </div>
          <button className="mgp-close" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
        </div>

        {/* Form de criação */}
        <div className="mgp-create">
          <div className="mgp-create-grid">
            <input
              className="mgp-input mgp-input-nome"
              placeholder="Nome do pagador *"
              value={novo.nome}
              onChange={(e) => setNovo((n) => ({ ...n, nome: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleCriar()}
            />
            <input
              className="mgp-input"
              placeholder="CPF/CNPJ (opcional)"
              value={novo.documento}
              onChange={(e) => setNovo((n) => ({ ...n, documento: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleCriar()}
            />
            <input
              className="mgp-input"
              placeholder="Observação (opcional)"
              value={novo.observacao}
              onChange={(e) => setNovo((n) => ({ ...n, observacao: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleCriar()}
            />
            <button className="mgp-add-btn" onClick={handleCriar} disabled={saving || !novo.nome.trim()}>
              {saving ? <FaSpinner className="mgp-spin" /> : <FaPlus />} Adicionar
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mgp-toolbar">
          <div className="mgp-search">
            <FaSearch />
            <input placeholder="Buscar pagador…" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <label className="mgp-check-inativos">
            <input
              type="checkbox"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
        </div>

        {/* Lista */}
        <div className="mgp-list">
          {loading ? (
            <div className="mgp-state"><FaSpinner className="mgp-spin" /> Carregando…</div>
          ) : filtrados.length === 0 ? (
            <div className="mgp-state">
              {busca ? 'Nenhum pagador encontrado.' : 'Nenhum pagador cadastrado ainda.'}
            </div>
          ) : (
            filtrados.map((p) => (
              <div className={`mgp-row ${!p.ativo ? 'inativo' : ''}`} key={p.id}>
                {editId === p.id ? (
                  <div className="mgp-edit">
                    <input
                      className="mgp-input"
                      value={editForm.nome}
                      onChange={(e) => setEditForm((f) => ({ ...f, nome: e.target.value }))}
                      placeholder="Nome *"
                    />
                    <input
                      className="mgp-input"
                      value={editForm.documento}
                      onChange={(e) => setEditForm((f) => ({ ...f, documento: e.target.value }))}
                      placeholder="CPF/CNPJ"
                    />
                    <input
                      className="mgp-input"
                      value={editForm.observacao}
                      onChange={(e) => setEditForm((f) => ({ ...f, observacao: e.target.value }))}
                      placeholder="Observação"
                    />
                    <div className="mgp-edit-actions">
                      <button className="mgp-icon-btn save" onClick={() => salvarEdicao(p.id)} disabled={saving} title="Salvar"><FaCheck /></button>
                      <button className="mgp-icon-btn" onClick={() => setEditId(null)} title="Cancelar"><FaTimes /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="mgp-avatar">{p.nome.charAt(0).toUpperCase()}</span>
                    <div className="mgp-info">
                      <div className="mgp-info-top">
                        <strong>{p.nome}</strong>
                        {!p.ativo && <span className="mgp-badge-inativo">Inativo</span>}
                      </div>
                      <div className="mgp-info-sub">
                        {p.documento  && <span className="mgp-doc">{p.documento}</span>}
                        {p.observacao && <span className="mgp-obs" title={p.observacao}>{p.observacao}</span>}
                      </div>
                    </div>
                    <div className="mgp-actions">
                      <button
                        className="mgp-icon-btn toggle"
                        onClick={() => toggleAtivo(p)}
                        disabled={saving}
                        title={p.ativo ? 'Inativar' : 'Reativar'}
                      >
                        {p.ativo ? <FaToggleOn className="on" /> : <FaToggleOff />}
                      </button>
                      <button className="mgp-icon-btn" onClick={() => iniciarEdicao(p)} title="Editar"><FaEdit /></button>
                      <button className="mgp-icon-btn del" onClick={() => excluir(p)} disabled={saving} title="Excluir"><FaTrash /></button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mgp-footer">
          <span className="mgp-count">{filtrados.length} pagador(es)</span>
          <button className="mgp-btn-close" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}